import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Upload, Download, RotateCcw, Trash2, Eye, Save, CheckCircle2 } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { PageTitle } from "@/components/admin/AdminLayout";

export const openPdf = async (request, name = "document.pdf") => {
  const w = window.open("", "_blank");
  try {
    const r = await request();
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    if (w) w.location.href = url;
    else { const a = document.createElement("a"); a.href = url; a.download = name; a.click(); }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    if (w) w.close();
    let msg = errMsg(e);
    try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* réponse non JSON */ }
    toast.error(msg);
  }
};
const sizeLabel = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} Mo` : `${Math.max(1, Math.round(n / 1024))} Ko`);

const Check = ({ cfg, setCfg, k, children, testId }) => (
  <label className="flex items-start gap-3 text-sm cursor-pointer"><input type="checkbox" className="w-5 h-5 accent-forest mt-0.5" checked={cfg[k] !== false} onChange={(e) => setCfg({ ...cfg, [k]: e.target.checked })} data-testid={testId} /><span>{children}</span></label>
);

function Automations() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/booking/settings").then((r) => setCfg(r.data.settings || r.data)); }, []);
  if (!cfg) return null;
  const save = async () => {
    setBusy(true);
    try { const r = await api.put("/admin/booking/settings", cfg); setCfg(r.data.settings || r.data); toast.success("Automatisations enregistrées"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="card p-5 sm:p-6" data-testid="docs-automations">
      <h2 className="font-serif text-2xl text-forest mb-1">Envois automatiques</h2>
      <p className="text-sm text-charcoal/60 mb-4">Vérifiés toutes les heures pour les réservations confirmées ou payées. Ils partent uniquement si l’envoi d’e-mails (SMTP) est configuré, et la caution si Stripe l’est aussi.</p>
      <div className="space-y-3">
        <Check cfg={cfg} setCfg={setCfg} k="auto_contract" testId="auto-contract">Contrat de location pré-rempli envoyé dès que la réservation est confirmée ou payée (PDF en pièce jointe).</Check>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span>Demande d’empreinte de caution envoyée</span>
          <input type="number" min="0" max="30" className="input w-20 py-1.5" value={cfg.security_days_before ?? 7} onChange={(e) => setCfg({ ...cfg, security_days_before: Number(e.target.value) })} data-testid="auto-security-days" />
          <span>jour(s) avant l’arrivée (0 = désactivé).</span>
        </div>
        {Number(cfg.security_days_before) > 5 && <p className="text-xs text-charcoal/60 bg-sand/40 rounded-lg px-3 py-2" data-testid="auto-security-warning">Bon à savoir : chez Stripe, une empreinte reste valable environ 7 jours après sa validation par le voyageur. Sa date d’expiration est indiquée dans chaque réservation.</p>}
        <Check cfg={cfg} setCfg={setCfg} k="auto_booklet" testId="auto-booklet">Livret d’accueil (version active ci-dessous) envoyé la veille de l’arrivée (J-1).</Check>
        <Check cfg={cfg} setCfg={setCfg} k="auto_review" testId="auto-review">Invitation à laisser un avis sur le site, envoyée le lendemain du départ (J+1). Les avis reçus sont à publier depuis « Avis ».</Check>
      </div>
      <button onClick={save} disabled={busy} className="btn-primary mt-4" data-testid="auto-save"><Save size={15} /> Enregistrer</button>
    </section>
  );
}

function ContractTemplate() {
  const [t, setT] = useState(null);
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/stay/contract-template").then((r) => setT(r.data)); }, []);
  if (!t) return null;
  const save = async () => {
    setBusy(true);
    try { const r = await api.put("/admin/stay/contract-template", { title: t.title, body: t.body }); setT(r.data); toast.success("Modèle de contrat enregistré"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Rétablir le modèle par défaut ? Vos modifications seront perdues.")) return;
    const r = await api.delete("/admin/stay/contract-template"); setT(r.data); toast.success("Modèle par défaut rétabli");
  };
  const insert = (p) => {
    const el = ref.current; const tag = `{${p}}`;
    if (!el) return setT({ ...t, body: t.body + tag });
    const s = el.selectionStart ?? t.body.length; const e = el.selectionEnd ?? s;
    setT({ ...t, body: t.body.slice(0, s) + tag + t.body.slice(e) });
    setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = s + tag.length; }, 0);
  };
  return (
    <section className="card p-5 sm:p-6" data-testid="docs-contract">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-serif text-2xl text-forest">Modèle de contrat</h2>
        <span className="text-xs text-charcoal/50">{t.is_default ? "Modèle par défaut" : `Modifié le ${fmtDateTime(t.updated_at)}`}</span>
      </div>
      <p className="text-sm text-charcoal/60 mt-1 mb-4">Les champs entre accolades sont remplacés par les informations de chaque réservation. « ## Titre » crée une section. Le texte entre crochets est à compléter : faites valider le contrat par un professionnel si besoin.</p>
      <label className="label" htmlFor="ct-title">Titre</label>
      <input id="ct-title" className="input" value={t.title} onChange={(e) => setT({ ...t, title: e.target.value })} data-testid="contract-title" />
      <label className="label mt-4" htmlFor="ct-body">Texte du contrat</label>
      <textarea id="ct-body" ref={ref} rows={18} className="input font-mono text-xs leading-relaxed" value={t.body} onChange={(e) => setT({ ...t, body: e.target.value })} data-testid="contract-body" />
      <div className="flex flex-wrap gap-1.5 mt-3" data-testid="contract-placeholders">
        {t.placeholders.map((p) => <button key={p} type="button" onClick={() => insert(p)} className="text-[11px] px-2 py-1 rounded-full bg-sage-100 text-forest hover:bg-sage-200">{`{${p}}`}</button>)}
      </div>
      <div className="flex flex-wrap gap-2 mt-5">
        <button onClick={save} disabled={busy} className="btn-primary" data-testid="contract-save"><Save size={15} /> Enregistrer</button>
        <button onClick={() => openPdf(() => api.post("/admin/stay/contract-template/preview", { title: t.title, body: t.body }, { responseType: "blob" }), "apercu-contrat.pdf")} className="btn-secondary" data-testid="contract-preview"><Eye size={15} /> Aperçu PDF</button>
        {!t.is_default && <button onClick={reset} className="btn-ghost" data-testid="contract-reset"><RotateCcw size={15} /> Modèle par défaut</button>}
      </div>
    </section>
  );
}

function Booklets() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const load = useCallback(() => api.get("/admin/stay/booklets").then((r) => setItems(r.data.items)), []);
  useEffect(() => { load(); }, [load]);
  const upload = async (file) => {
    if (!file) return;
    if (file.type && file.type !== "application/pdf") return toast.error("Le fichier doit être un PDF");
    if (file.size > 15 * 1024 * 1024) return toast.error("PDF trop volumineux (15 Mo maximum)");
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { await api.post("/admin/stay/booklets", fd); toast.success("Nouveau livret actif — l’ancien est archivé"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); if (input.current) input.current.value = ""; }
  };
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(errMsg(e)); } };
  const active = items?.find((x) => x.active);
  const archived = (items || []).filter((x) => !x.active);
  return (
    <section className="card p-5 sm:p-6" data-testid="docs-booklets">
      <h2 className="font-serif text-2xl text-forest mb-1">Livret d’accueil</h2>
      <p className="text-sm text-charcoal/60 mb-4">Chaque nouveau PDF devient la version active (envoyée à J-1) ; les précédentes sont conservées dans l’historique.</p>
      {active ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="booklet-active">
          <div className="flex items-center gap-3"><CheckCircle2 size={20} className="text-emerald-600" />
            <div><p className="font-medium text-forest">{active.filename} <span className="text-xs text-charcoal/50">· version {active.version}</span></p>
              <p className="text-xs text-charcoal/60">Actif depuis le {fmtDateTime(active.uploaded_at)} · {sizeLabel(active.size)}</p></div></div>
          <button onClick={() => openPdf(() => api.get(`/admin/stay/booklets/${active.id}/file`, { responseType: "blob" }), active.filename)} className="btn-ghost px-3 py-2 min-h-0 text-xs" data-testid="booklet-active-view"><Eye size={13} /> Voir</button>
        </div>
      ) : items && <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2" data-testid="booklet-none">Aucun livret actif : rien n’est envoyé à J-1 tant qu’aucun PDF n’est téléversé.</p>}
      <input ref={input} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="booklet-file" />
      <button onClick={() => input.current?.click()} disabled={busy} className="btn-primary mt-4" data-testid="booklet-upload"><Upload size={15} /> {busy ? "Envoi…" : active ? "Téléverser une nouvelle version" : "Téléverser le livret (PDF)"}</button>
      {archived.length > 0 && (
        <div className="mt-6">
          <h3 className="eyebrow mb-2">Historique archivé</h3>
          <ul className="divide-y divide-sand text-sm border border-sand rounded-xl" data-testid="booklet-history">
            {archived.map((x) => (
              <li key={x.id} className="px-4 py-3 flex flex-wrap items-center justify-between gap-2" data-testid={`booklet-row-${x.id}`}>
                <div><p className="text-forest">{x.filename} <span className="text-xs text-charcoal/50">· v{x.version}</span></p>
                  <p className="text-xs text-charcoal/50">Téléversé le {fmtDateTime(x.uploaded_at)} · archivé le {fmtDateTime(x.archived_at)} · {sizeLabel(x.size)}</p></div>
                <div className="flex gap-1.5">
                  <button onClick={() => openPdf(() => api.get(`/admin/stay/booklets/${x.id}/file`, { responseType: "blob" }), x.filename)} className="btn-ghost px-2.5 py-1.5 min-h-0 text-xs" data-testid={`booklet-view-${x.id}`}><Download size={13} /> Voir</button>
                  <button onClick={() => window.confirm(`Réactiver la version ${x.version} ? La version active sera archivée.`) && act(() => api.post(`/admin/stay/booklets/${x.id}/activate`), "Version réactivée")} className="btn-ghost px-2.5 py-1.5 min-h-0 text-xs" data-testid={`booklet-activate-${x.id}`}><RotateCcw size={13} /> Réactiver</button>
                  <button onClick={() => window.confirm(`Supprimer définitivement la version ${x.version} ?`) && act(() => api.delete(`/admin/stay/booklets/${x.id}`), "Version supprimée")} className="btn-ghost px-2.5 py-1.5 min-h-0 text-xs text-red-600" aria-label="Supprimer" data-testid={`booklet-delete-${x.id}`}><Trash2 size={13} /></button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

const OWNER_FIELDS = [
  { k: "owner_name", label: "Nom du propriétaire / raison sociale" }, { k: "legal_status", label: "Statut (particulier, SCI, SARL…)" },
  { k: "siret", label: "SIRET (si société)" }, { k: "owner_address", label: "Adresse du propriétaire" },
  { k: "owner_email", label: "E-mail" }, { k: "owner_phone", label: "Téléphone" },
];
const TERMS_FIELDS = [
  { k: "cancellation_policy", label: "Conditions d’annulation", rows: 5 }, { k: "house_rules", label: "Règlement intérieur", rows: 5 },
  { k: "tourist_tax", label: "Taxe de séjour", rows: 2 }, { k: "other_conditions", label: "Autres conditions particulières", rows: 3 },
];

function OwnerForm() {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/stay/owner").then((r) => setF(r.data)).catch(() => setF({})); }, []);
  if (!f) return null;
  const save = async () => {
    setBusy(true);
    try { const r = await api.put("/admin/stay/owner", f); setF(r.data); toast.success("Coordonnées et conditions enregistrées"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="card p-5 sm:p-6" data-testid="docs-owner">
      <h2 className="font-serif text-xl text-forest mb-1">Propriétaire & conditions</h2>
      <p className="text-xs text-charcoal/60 mb-4">Saisis une fois pour toutes : ils remplissent automatiquement chaque contrat (et l’identité du propriétaire est reprise dans les mentions légales du site). Un champ vide apparaît « [À compléter] » dans le contrat.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {OWNER_FIELDS.map((x) => (
          <div key={x.k}><label className="label">{x.label}</label>
            <input className="input" value={f[x.k] || ""} onChange={(e) => setF({ ...f, [x.k]: e.target.value })} data-testid={`owner-${x.k}`} /></div>
        ))}
      </div>
      <div className="grid gap-3 mt-3">
        {TERMS_FIELDS.map((x) => (
          <div key={x.k}><label className="label">{x.label}</label>
            <textarea rows={x.rows} className="input" value={f[x.k] || ""} onChange={(e) => setF({ ...f, [x.k]: e.target.value })} data-testid={`owner-${x.k}`} /></div>
        ))}
      </div>
      <button onClick={save} disabled={busy} className="btn-primary mt-4" data-testid="owner-save">{busy ? "Enregistrement…" : "Enregistrer"}</button>
      <p className="text-xs text-charcoal/50 mt-3">Si vous avez personnalisé le modèle de contrat avant cette mise à jour, ajoutez-y les champs {"{owner_address}"}, {"{owner_contact}"}, {"{cancellation_policy}"}, {"{house_rules}"}, {"{tourist_tax}"} et {"{other_conditions}"} (ou rétablissez le modèle par défaut).</p>
    </section>
  );
}

export default function DocumentsAdmin() {
  return (
    <div data-testid="admin-documents">
      <PageTitle title="Contrat & livret d’accueil" text="Documents envoyés automatiquement aux voyageurs des réservations du site." />
      <div className="space-y-6">
        <Automations />
        <OwnerForm />
        <ContractTemplate />
        <Booklets />
      </div>
      <p className="text-xs text-charcoal/40 mt-6 flex items-center gap-1.5"><FileText size={12} /> Les documents sont envoyés dans la langue du voyageur (e-mail) ; le contrat est établi en français.</p>
    </div>
  );
}
